<template>
  <div class="member-analysis-container">
    <el-card class="filter-container" shadow="never">
      <div class="filter-item">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          align="right"
          :range-separator="$t('memberAnalysis.to')"
          :start-placeholder="$t('memberAnalysis.startDate')"
          :end-placeholder="$t('memberAnalysis.endDate')"
          :picker-options="pickerOptions"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd">
        </el-date-picker>
        <el-button type="primary" @click="fetchData" style="margin-left: 10px;">{{ $t('memberAnalysis.search') }}</el-button>
      </div>
    </el-card>
    
    <el-row :gutter="20" style="margin-top: 20px;">
      <!-- 会员概况 -->
      <el-col :span="24">
        <el-card shadow="hover">
          <div slot="header" class="clearfix">
            <span>{{ $t('memberAnalysis.memberOverview') }}</span>
          </div>
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="stat-item">
                <div class="stat-value">{{ statistics.totalMembers || 0 }}</div>
                <div class="stat-title">{{ $t('memberAnalysis.totalMembers') }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-item">
                <div class="stat-value">{{ statistics.activeMembers || 0 }}</div>
                <div class="stat-title">{{ $t('memberAnalysis.activeMembers') }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-item">
                <div class="stat-value">{{ statistics.newMembers || 0 }}</div>
                <div class="stat-title">{{ $t('memberAnalysis.newMembers') }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-item">
                <div class="stat-value">{{ activeRate }}%</div>
                <div class="stat-title">{{ $t('memberAnalysis.activeRate') }}</div>
              </div>
            </el-col>
          </el-row>
        </el-card>
      </el-col>
    </el-row>
    
    <el-row :gutter="20" style="margin-top: 20px;">
      <!-- 会员增长趋势 -->
      <el-col :span="12">
        <el-card shadow="hover">
          <div slot="header" class="clearfix">
            <span>{{ $t('memberAnalysis.memberGrowthTrend') }}</span>
            <el-radio-group v-model="growthPeriod" size="mini" style="float: right;">
              <el-radio-button label="day">{{ $t('memberAnalysis.day') }}</el-radio-button>
              <el-radio-button label="week">{{ $t('memberAnalysis.week') }}</el-radio-button>
              <el-radio-button label="month">{{ $t('memberAnalysis.month') }}</el-radio-button>
            </el-radio-group>
          </div>
          <div class="chart-container">
            <div ref="growthChart" style="width: 100%; height: 350px;"></div>
          </div>
        </el-card>
      </el-col>
      
      <!-- 会员性别分布 -->
      <el-col :span="12">
        <el-card shadow="hover">
          <div slot="header" class="clearfix">
            <span>{{ $t('memberAnalysis.genderDistribution') }}</span>
          </div>
          <div class="chart-container">
            <div ref="genderChart" style="width: 100%; height: 350px;"></div>
          </div>
        </el-card>
      </el-col>
    </el-row>
    
    <el-row :gutter="20" style="margin-top: 20px;">
      <!-- 会员活跃度分布 -->
      <el-col :span="12">
        <el-card shadow="hover">
          <div slot="header" class="clearfix">
            <span>{{ $t('memberAnalysis.memberActivityDistribution') }}</span>
            <el-radio-group v-model="activityPeriod" size="mini" style="float: right;">
              <el-radio-button label="week">{{ $t('memberAnalysis.week') }}</el-radio-button>
              <el-radio-button label="month">{{ $t('memberAnalysis.month') }}</el-radio-button>
              <el-radio-button label="year">{{ $t('memberAnalysis.year') }}</el-radio-button>
            </el-radio-group>
          </div>
          <div class="chart-container">
            <div ref="activityChart" style="width: 100%; height: 350px;"></div>
          </div>
        </el-card>
      </el-col>
      
      <!-- 会员留存率 -->
      <el-col :span="12">
        <el-card shadow="hover">
          <div slot="header" class="clearfix">
            <span>{{ $t('memberAnalysis.retentionRate') }}</span>
          </div>
          <div class="chart-container">
            <div ref="retentionChart" style="width: 100%; height: 350px;"></div>
          </div>
        </el-card>
      </el-col>
    </el-row>
    
    <!-- 会员预约频率分析 -->
    <el-card shadow="hover" style="margin-top: 20px;">
      <div slot="header" class="clearfix">
        <span>{{ $t('memberAnalysis.bookingFrequencyAnalysis') }}</span>
      </div>
      <el-table :data="frequencyData" style="width: 100%">
        <el-table-column prop="frequency_range" :label="$t('memberAnalysis.bookingFrequency')" width="180"></el-table-column>
        <el-table-column prop="member_count" :label="$t('memberAnalysis.memberCount')" width="120"></el-table-column>
        <el-table-column prop="percentage" :label="$t('memberAnalysis.percentage')" width="120">
          <template slot-scope="scope">
            {{ scope.row.percentage }}%
          </template>
        </el-table-column>
        <el-table-column :label="$t('memberAnalysis.percentageChart')">
          <template slot-scope="scope">
            <el-progress :percentage="scope.row.percentage" :color="getProgressColor(scope.row.frequency_range)"></el-progress>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getMemberStatistics, getMemberActivityDistribution, getMemberGrowthTrend } from '@/api/statistics'

export default {
  name: 'MemberAnalysis',
  data() {
    return {
      pickerOptions: {
        shortcuts: []
      },
      dateRange: [
        this.formatDate(new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000)),
        this.formatDate(new Date())
      ],
      statistics: {},
      growthChart: null,
      genderChart: null,
      activityChart: null,
      retentionChart: null,
      growthPeriod: 'month',
      activityPeriod: 'month',
      growthData: [],
      activityData: [],
      frequencyData: [
        { frequency_range: '0次/月', member_count: 0, percentage: 0 },
        { frequency_range: '1-2次/月', member_count: 0, percentage: 0 },
        { frequency_range: '3-5次/月', member_count: 0, percentage: 0 },
        { frequency_range: '6-10次/月', member_count: 0, percentage: 0 },
        { frequency_range: '10次以上/月', member_count: 0, percentage: 0 }
      ]
    }
  },
  computed: {
    activeRate() {
      if (!this.statistics.totalMembers) return 0
      return ((this.statistics.activeMembers / this.statistics.totalMembers) * 100).toFixed(2)
    }
  },
  created() {
    // 初始化快捷方式
    this.pickerOptions.shortcuts = [
      {
        text: this.$t('memberAnalysis.shortcuts.lastWeek'),
        onClick: (picker) => {
          const end = new Date()
          const start = new Date()
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
          picker.$emit('pick', [start, end])
        }
      },
      {
        text: this.$t('memberAnalysis.shortcuts.lastMonth'),
        onClick: (picker) => {
          const end = new Date()
          const start = new Date()
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
          picker.$emit('pick', [start, end])
        }
      },
      {
        text: this.$t('memberAnalysis.shortcuts.lastThreeMonths'),
        onClick: (picker) => {
          const end = new Date()
          const start = new Date()
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
          picker.$emit('pick', [start, end])
        }
      }
    ]
  },
  mounted() {
    this.initCharts()
    this.fetchData()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    if (this.growthChart) this.growthChart.dispose()
    if (this.genderChart) this.genderChart.dispose()
    if (this.activityChart) this.activityChart.dispose()
    if (this.retentionChart) this.retentionChart.dispose()
  },
  watch: {
    growthPeriod() {
      this.fetchGrowthData()
    },
    activityPeriod() {
      this.fetchActivityData()
    }
  },
  methods: {
    formatDate(date) {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    fetchData() {
      const [startDate, endDate] = this.dateRange
      getMemberStatistics({ startDate, endDate }).then(response => {
        this.statistics = response.data.data
        this.updateGenderChart()
        
        // 模拟会员预约频率数据
        this.simulateFrequencyData()
      })
      
      this.fetchGrowthData()
      this.fetchActivityData()
      this.initRetentionChart() // 模拟留存率数据
    },
    fetchGrowthData() {
      getMemberGrowthTrend(this.growthPeriod, 12).then(response => {
        this.growthData = response.data.data
        this.updateGrowthChart()
      })
    },
    fetchActivityData() {
      getMemberActivityDistribution(this.activityPeriod).then(response => {
        this.activityData = response.data.data
        this.updateActivityChart()
      })
    },
    initCharts() {
      this.growthChart = echarts.init(this.$refs.growthChart)
      this.genderChart = echarts.init(this.$refs.genderChart)
      this.activityChart = echarts.init(this.$refs.activityChart)
      this.retentionChart = echarts.init(this.$refs.retentionChart)
    },
    updateGrowthChart() {
      if (!this.growthChart) return
      
      const xData = this.growthData.map(item => item.date_period)
      const newMemberData = this.growthData.map(item => item.new_members)
      const cumulativeData = this.growthData.map(item => item.cumulative_members)
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: [this.$t('memberAnalysis.newMembers'), this.$t('memberAnalysis.accumulatedMembers')]
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xData
        },
        yAxis: [
          {
            type: 'value',
            name: this.$t('memberAnalysis.newMembers'),
            position: 'left'
          },
          {
            type: 'value',
            name: this.$t('memberAnalysis.accumulatedMembers'),
            position: 'right'
          }
        ],
        series: [
          {
            name: this.$t('memberAnalysis.newMembers'),
            type: 'bar',
            data: newMemberData
          },
          {
            name: this.$t('memberAnalysis.accumulatedMembers'),
            type: 'line',
            yAxisIndex: 1,
            data: cumulativeData
          }
        ]
      }
      
      this.growthChart.setOption(option)
    },
    updateGenderChart() {
      if (!this.genderChart || !this.statistics.genderDistribution) return
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          data: this.statistics.genderDistribution.map(item => {
            return item.gender === 'M' ? this.$t('memberAnalysis.male') : item.gender === 'F' ? this.$t('memberAnalysis.female') : this.$t('memberAnalysis.unknown')
          })
        },
        series: [
          {
            name: this.$t('memberAnalysis.memberGender'),
            type: 'pie',
            radius: ['50%', '70%'],
            avoidLabelOverlap: false,
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: this.statistics.genderDistribution.map(item => {
              return {
                name: item.gender === 'M' ? this.$t('memberAnalysis.male') : item.gender === 'F' ? this.$t('memberAnalysis.female') : this.$t('memberAnalysis.unknown'),
                value: item.count
              }
            }),
            color: ['#5470c6', '#ee6666', '#91cc75']
          }
        ]
      }
      
      this.genderChart.setOption(option)
    },
    updateActivityChart() {
      if (!this.activityChart || !this.activityData.length) return
      
      const activityLevels = this.activityData.map(item => item.activity_level)
      const memberCounts = this.activityData.map(item => item.member_count)
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          data: activityLevels
        },
        series: [
          {
            name: this.$t('memberAnalysis.memberActivity'),
            type: 'pie',
            radius: ['50%', '70%'],
            avoidLabelOverlap: false,
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: activityLevels.map((level, index) => ({
              value: memberCounts[index],
              name: level
            })),
            color: ['#91cc75', '#fac858', '#ee6666', '#73c0de']
          }
        ]
      }
      
      this.activityChart.setOption(option)
    },
    initRetentionChart() {
      if (!this.retentionChart) return
      
      // 模拟会员留存率数据
      const months = ['1月', '2月', '3月', '4月', '5月', '6月']
      const retentionRates = [
        [100, 80, 70, 65, 60, 55],
        [100, 85, 75, 70, 65, 60],
        [100, 75, 65, 60, 55, 50],
        [100, 90, 80, 75, 70, 65],
        [100, 85, 75, 65, 60, 55],
        [100, 80, 70, 65, 60, 55]
      ]
      
      const xLabels = [
        this.$t('memberAnalysis.currentMonth'),
        this.$t('memberAnalysis.afterMonths', { n: 1 }),
        this.$t('memberAnalysis.afterMonths', { n: 2 }),
        this.$t('memberAnalysis.afterMonths', { n: 3 }),
        this.$t('memberAnalysis.afterMonths', { n: 4 }),
        this.$t('memberAnalysis.afterMonths', { n: 5 })
      ]
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: months
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xLabels
        },
        yAxis: {
          type: 'value',
          name: this.$t('memberAnalysis.retentionRate') + '(%)'
        },
        series: months.map((month, index) => ({
          name: month,
          type: 'line',
          data: retentionRates[index]
        }))
      }
      
      this.retentionChart.setOption(option)
    },
    simulateFrequencyData() {
      // 模拟会员预约频率数据
      const total = this.statistics.totalMembers || 100
      
      this.frequencyData = [
        { frequency_range: '0次/月', member_count: Math.floor(total * 0.2), percentage: 20 },
        { frequency_range: '1-2次/月', member_count: Math.floor(total * 0.3), percentage: 30 },
        { frequency_range: '3-5次/月', member_count: Math.floor(total * 0.25), percentage: 25 },
        { frequency_range: '6-10次/月', member_count: Math.floor(total * 0.15), percentage: 15 },
        { frequency_range: '10次以上/月', member_count: Math.floor(total * 0.1), percentage: 10 }
      ]
    },
    getProgressColor(range) {
      const colors = {
        '0次/月': '#909399',
        '1-2次/月': '#e6a23c',
        '3-5次/月': '#67c23a',
        '6-10次/月': '#409eff',
        '10次以上/月': '#f56c6c'
      }
      return colors[range] || '#409eff'
    },
    resizeCharts() {
      if (this.growthChart) this.growthChart.resize()
      if (this.genderChart) this.genderChart.resize()
      if (this.activityChart) this.activityChart.resize()
      if (this.retentionChart) this.retentionChart.resize()
    }
  }
}
</script>

<style lang="scss" scoped>
.member-analysis-container {
  padding: 20px;
  
  .filter-container {
    .filter-item {
      display: flex;
      align-items: center;
    }
  }
  
  .stat-item {
    text-align: center;
    padding: 10px;
    
    .stat-value {
      font-size: 24px;
      font-weight: bold;
      color: #409EFF;
    }
    
    .stat-title {
      font-size: 14px;
      color: #606266;
      margin-top: 5px;
    }
  }
  
  .chart-container {
    position: relative;
    width: 100%;
    height: 350px;
  }
}
</style> 